import { useEffect } from "react";
import { connect } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";

import * as actions from "../../store/actions/index";
import { withRouter } from "../../shared/utility";

import EmptyModal from "../../components/modals/EmptyModal";
import PurchaseOrderHeaderForm from "../../components/forms/purchaseOrders/PurchaseOrderHeaderForm";
import PurchaseOrderLineItemsTable from "../../components/forms/purchaseOrders/PurchaseOrderLineItemsTable";
import PurchaseOrderSummaryPanel from "../../components/forms/purchaseOrders/PurchaseOrderSummaryPanel";
import PurchaseOrderReceivingPanel from "../../components/forms/purchaseOrders/PurchaseOrderReceivingPanel";
import {
  PurchaseOrderFooterActions,
  PurchaseOrderHeaderActions,
} from "../../components/forms/purchaseOrders/PurchaseOrderActionBar";
import PurchaseOrderSavingOverlay from "../../components/forms/purchaseOrders/PurchaseOrderSavingOverlay";
import { useCreatePurchaseOrderViewModel } from "../../viewmodels/purchaseOrders/useCreatePurchaseOrderViewModel";
import FormSectionLoadingSkeleton from "../../components/operational/FormSectionLoadingSkeleton";

function CreatePurchaseOrder(props) {
  const { logout, addSnackbar } = props;
  const navigate = useNavigate();
  const { purchaseOrderId: routePurchaseOrderId } = useParams();
  const vm = useCreatePurchaseOrderViewModel(logout, addSnackbar);

  const isNew = !vm.purchaseOrderId && !routePurchaseOrderId;

  useEffect(() => {
    vm.init(routePurchaseOrderId);
  }, [routePurchaseOrderId]);

  useEffect(() => {
    if (vm.navigateDestination) {
      navigate(vm.navigateDestination, { replace: true });
    }
  }, [vm.navigateDestination, navigate]);

  const title = isNew
    ? "Create purchase order"
    : vm.poNumber
      ? vm.poNumber
      : "Purchase order";

  const saveLabel = isNew ? "Save draft" : "Save changes";
  const busy = vm.saving || vm.actionLoading;

  const actionPermissions = {
    canApprove: vm.canApprove,
    canReceive: vm.canReceive,
    canClose: vm.canClose,
    canReopen: vm.canReopen,
  };

  const footerActions = (
    <PurchaseOrderFooterActions
      isNew={isNew}
      busy={busy}
      loading={vm.loading}
      saving={vm.saving}
      actionLoading={vm.actionLoading}
      saveLabel={saveLabel}
      onSave={() => vm.saveChanges()}
      onCreateAndApprove={() => vm.createAndApprove()}
      onApprove={() => vm.approve()}
      {...actionPermissions}
    />
  );

  return (
    <div className="min-h-screen bg-[#F6F8FB] px-4 py-5 sm:px-6 lg:px-8">
      <div className="mx-auto max-w-7xl space-y-5 pb-24">
        <section className="rounded-2xl border border-[#E2E8F0] bg-white p-5 shadow-sm">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
            <div>
              <p className="text-xs font-semibold uppercase tracking-wide text-[#2563EB]">
                QA Trace / Purchasing
              </p>
              <h1 className="mt-1 text-2xl font-bold text-[#0F172A]">
                {title}
              </h1>
              <p className="mt-1 text-sm text-[#64748B]">
                {isNew
                  ? "Build an expected supplier order before receiving inventory into the plant."
                  : "Update this purchase order, line items, and delivery details."}
              </p>
            </div>
            <PurchaseOrderHeaderActions
              purchaseOrderId={vm.purchaseOrderId}
              poStatus={vm.poStatus}
              poNumber={vm.poNumber}
              busy={busy}
              onApprove={() => vm.approve()}
              onClose={() => vm.closePo()}
              onReopen={() => vm.reopen()}
              {...actionPermissions}
            />
          </div>
          <div className="mt-4 hidden flex-wrap justify-end gap-2 sm:flex">
            {footerActions}
          </div>
        </section>

        {vm.loading ? (
          <div className="rounded-2xl border border-[#E2E8F0] bg-white p-8">
            <FormSectionLoadingSkeleton rows={8} />
          </div>
        ) : (
          <div className="space-y-5">
            <div className="grid grid-cols-1 gap-5 lg:grid-cols-[1fr_280px] lg:items-start">
              <PurchaseOrderHeaderForm
                compact
                purchaseOrder={vm.purchaseOrder}
                supplierOptions={vm.supplierOptions}
                locationOptions={vm.locationOptions}
                onSupplierChange={vm.onSupplierChange}
                onLocationChange={vm.onLocationChange}
                setPurchaseOrderField={vm.setPurchaseOrderField}
              />
              <div className="flex flex-col gap-5">
                {vm.summary && (
                  <PurchaseOrderReceivingPanel
                    summary={vm.summary}
                    receivingInvoices={vm.receivingInvoices}
                  />
                )}
                <PurchaseOrderSummaryPanel
                  compact
                  lineSummary={vm.lineSummary}
                />
              </div>
            </div>
            <PurchaseOrderLineItemsTable
              lineItems={vm.lineItemsWithTotals}
              assemblyItemOptions={vm.assemblyItemOptions}
              onLineItemAssemblySelect={vm.onLineItemAssemblySelect}
              updateLineItem={vm.updateLineItem}
              removeLineItem={vm.removeLineItem}
              addLineItem={vm.addLineItem}
            />
          </div>
        )}
      </div>

      <div className="fixed bottom-0 left-0 right-0 z-20 border-t border-[#E2E8F0] bg-white px-4 py-3 shadow-lg sm:hidden">
        <div className="flex flex-col gap-2">{footerActions}</div>
      </div>

      <EmptyModal
        childComponent={vm.emptyModal.childComponent}
        open={vm.emptyModal.open}
        setOpen={vm.setEmptyModelOpen}
      />

      {vm.saving && isNew && <PurchaseOrderSavingOverlay />}
    </div>
  );
}

const mapStateToProps = () => ({});

const mapDispatchToProps = (dispatch) => ({
  logout: () => dispatch(actions.logout()),
  addSnackbar: (snackbar) => dispatch(actions.addSnackbar(snackbar)),
});

export default withRouter(
  connect(mapStateToProps, mapDispatchToProps)(CreatePurchaseOrder),
);
